import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import { BrandedPagesCustomSettings } from './custom-settings';
import EmailInput from './email-input';
import ImageInput from './image-input';
import SocialInput from './social-input';
import style from './style.module.less';
import { FeatureGatedPromotionalBanner } from '../../../../../feature-gates/feature-gated-promotional-banner';
import { SENDER_EMAILS_PATH } from '../../../../../routes/constants';
import { fieldToErrorMap, errorToMessageMap } from '../../../api/store-api-error-mapper';
import { useIsBrandingTrackingEnabledForSeller } from '../../../branding-defaults/use-is-branding-tracking-enabled-for-seller';
import { useIsSenderEmailsEnabledForSeller } from '../../../sender-emails/use-is-sender-emails-enabled-for-seller';
import { EmailSettingsSection } from '../../../stores/selected-store/returns-tab/email-settings-section';
import { useStoreUpdateContext } from '../context';
import messages from '../messages';
import storeStyle from '../style.module.less';

const getCompanyNameErrorMessage = (storeValidationErrors) => {
  return storeValidationErrors[fieldToErrorMap.companyName] ? (
    <FormattedMessage {...errorToMessageMap[fieldToErrorMap.companyName]} />
  ) : undefined;
};

const getPhoneInlineMessage = (storeValidationErrors) => {
  return storeValidationErrors[fieldToErrorMap.phone] ? (
    <FormattedMessage {...errorToMessageMap[fieldToErrorMap.phone]} />
  ) : (
    <FormattedMessage {...messages.phoneInfo} />
  );
};

/**
 * @param {object} props
 * @param {string} props.storeGuid
 * @param {object} props.brandingSettings
 * @param {object} props.customBranding
 * @param {string|null} [props.returnMessage]
 * @param {string|null} [props.emailMessage]
 */
export const BrandingTab = ({ storeGuid, brandingSettings, customBranding, returnMessage = null, emailMessage = null }) => {
  const { resetValidationError, storeValidationErrors, updateStoreSetting } = useStoreUpdateContext();
  const { companyName, useCompanyNameOnLabel, phone, email, website, facebookProfile, instagramProfile, twitterProfile, logoUri } =
    brandingSettings;

  const isSenderEmailsEnabled = useIsSenderEmailsEnabledForSeller();
  const { hasBaseFeatures: hasBrandedTrackingFeature } = useIsBrandingTrackingEnabledForSeller();

  const GROUP_NAME = 'brandingSettings';
  const updateCompanyName = (e) => {
    updateStoreSetting(GROUP_NAME, 'companyName', e.target.value);
    resetValidationError(fieldToErrorMap.companyName);
  };
  const updateUseCompanyNameOnLabel = (val) => updateStoreSetting(GROUP_NAME, 'useCompanyNameOnLabel', val);
  const updatePhone = (e) => {
    updateStoreSetting(GROUP_NAME, 'phone', e.target.value);
    resetValidationError(fieldToErrorMap.phone);
  };
  const updateEmail = (e) => {
    updateStoreSetting(GROUP_NAME, 'email', e.target.value);
    resetValidationError(fieldToErrorMap.email);
  };

  const updateWebsite = (e) => {
    updateStoreSetting(GROUP_NAME, 'website', e.target.value);
  };

  const updateFacebook = (val) => updateStoreSetting(GROUP_NAME, 'facebookProfile', val);
  const updateInsta = (val) => updateStoreSetting(GROUP_NAME, 'instagramProfile', val);
  const updateTwitter = (val) => updateStoreSetting(GROUP_NAME, 'twitterProfile', val);
  const logoImageChangeCallback = (newLogoUri) => updateStoreSetting(GROUP_NAME, 'logoUri', newLogoUri);

  return (
    <div className={classnames(storeStyle['tab-content'], style['branding-tab'])}>
      <FlexColumn className={style['general-branding-settings-text']}>
        <Text element='span' textType={TextType.H1} value={messages.brandingPageTitle} />
        <Text textType={TextType.Paragraph} value={messages.brandingInstructionsLine1} />
        {hasBrandedTrackingFeature && isSenderEmailsEnabled ? (
          <>
            <Text
              formattedMessageValues={{
                a: (chunks) => <Link to={SENDER_EMAILS_PATH}>{chunks}</Link>,
              }}
              textType={TextType.Paragraph}
              value={messages.brandingInstructionsLine2}
            />
            <BannerMessageV2
              content={<Text textType={TextType.Paragraph} value={messages.brandingInstructionsBanner} />}
              layout={BannerLayout.Full}
              status={BannerStatus.Info}
            />
          </>
        ) : null}
        {!hasBrandedTrackingFeature && <FeatureGatedPromotionalBanner className={style['promo-banner']} />}
      </FlexColumn>
      <div className={classnames(storeStyle['grid-form'], style['branding-tab__general-branding-settings'])}>
        <div className={storeStyle['multi-line-label']}>
          <FormattedMessage {...messages.companyName} />
        </div>
        <div className={storeStyle['input-element']}>
          <Input
            inlineMessage={getCompanyNameErrorMessage(storeValidationErrors)}
            inlineMessageType='error'
            name='companyName'
            onChange={updateCompanyName}
            value={companyName ?? ''}
            version={version2}
          />
          <Checkbox
            checked={useCompanyNameOnLabel}
            className={storeStyle['sub-checkbox']}
            label={<FormattedMessage {...messages.alwaysUseCompanyName} />}
            onChange={updateUseCompanyNameOnLabel}
          />
        </div>

        <div className={storeStyle['input-label']}>
          <FormattedMessage {...messages.phone} />
        </div>
        <div className={storeStyle['input-element']}>
          <Input
            inlineMessage={getPhoneInlineMessage(storeValidationErrors)}
            inlineMessageType={storeValidationErrors[fieldToErrorMap.phone] ? 'error' : 'info'}
            name='phone'
            onChange={updatePhone}
            value={phone ?? ''}
            version={version2}
          />
        </div>
        <EmailInput email={email} updateEmail={updateEmail} />
        <div className={storeStyle['multi-line-label']}>
          <FormattedMessage {...messages.storeLogo} />
        </div>
        <div className={storeStyle['input-element']}>
          <ImageInput logoUri={logoUri} onChange={logoImageChangeCallback} storeGuid={storeGuid} />
        </div>

        <div className={storeStyle['input-label']}>
          <FormattedMessage {...messages.website} />
        </div>
        <div className={storeStyle['input-element']}>
          <Input onChange={updateWebsite} value={website} version={version2} />
        </div>

        <div className={storeStyle['multi-line-label']}>
          <FormattedMessage {...messages.socialLinks} />
        </div>
        <div className={storeStyle['input-element']}>
          <SocialInput network='facebook' onChange={updateFacebook} value={facebookProfile} />
          <SocialInput network='twitter' onChange={updateTwitter} value={twitterProfile} />
          <SocialInput network='instagram' onChange={updateInsta} value={instagramProfile} />
        </div>
        <div className={storeStyle['input-label']}>
          <FormattedMessage {...messages.emailSettings} />
        </div>
        <div className={storeStyle['input-element']}>
          <EmailSettingsSection emailMessage={emailMessage} heading='' />
        </div>
      </div>
      <BrandedPagesCustomSettings brandingSettings={brandingSettings} customBranding={customBranding} returnMessage={returnMessage} />
    </div>
  );
};
